import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Link2,
  Globe,
  ExternalLink,
  Copy,
  Check,
  Server,
  Info,
  Shuffle,
  AlertTriangle,
  Activity,
  Zap,
  Shield,
  Plus,
  Trash2,
  X,
  Save,
  BarChart3,
  ToggleLeft,
  ToggleRight,
  Bell,
  Route,
  Lock,
  Search,
  Loader2
} from "lucide-react";
import { CustomDomainsPanel } from "@/components/CustomDomainsPanel";
import { CampaignToolbar } from "@/components/redirector/CampaignToolbar";
import { CampaignLinksMaster } from "@/components/redirector/CampaignLinksMaster";
import { CampaignLinkDetailPane } from "@/components/redirector/CampaignLinkDetailPane";
import { DomainSplitWorkspace } from "@/components/redirector/DomainSplitWorkspace";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { filterCampaigns, groupLinksIntoCampaigns } from "@/lib/campaignGrouping";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const freshLinkForm = (domain = "") => ({
  domain,
  domains: domain ? [domain] : [],
  destination: "",
  name: "",
  campaign_type: "direct",
  pass_email: true,
  pass_email_format: "base64",
  track_b2b: false,
});

const RedirectorLinks = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState({
    redirect_url: "",
    current_preview: "blank",
    redirect_domains: [],
    rotation_enabled: true,
    rotation_mode: "smart"
  });
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedItem, setCopiedItem] = useState(null);
  const [rotationEnabled, setRotationEnabled] = useState(true);
  const [rotationMode, setRotationMode] = useState("smart");
  const [activeTab, setActiveTab] = useState("links"); // links, domains, settings
  
  // Link form state
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [linkForm, setLinkForm] = useState(() => freshLinkForm());
  
  // Bulk selection state
  const [selectedLinks, setSelectedLinks] = useState(new Set());
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [retryingLinkId, setRetryingLinkId] = useState(null);
  const [syncFeedback, setSyncFeedback] = useState(null);
  const [linksSearch, setLinksSearch] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingLink, setDeletingLink] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  
  // Bulk campaign type state
  const [bulkCampaignType, setBulkCampaignType] = useState("direct");
  const [updatingCampaignType, setUpdatingCampaignType] = useState(false);

  // Custom paths state
  const [customPaths, setCustomPaths] = useState([]);
  const [globalPaths, setGlobalPaths] = useState([]);
  const [maxCustomPaths, setMaxCustomPaths] = useState(3);
  const [usedCustomPaths, setUsedCustomPaths] = useState(0);
  const [newCustomPath, setNewCustomPath] = useState("");
  const [customPathLoading, setCustomPathLoading] = useState(false);

  // Domain selection state
  const [availableDomains, setAvailableDomains] = useState([]);
  const [domainLimits, setDomainLimits] = useState({ max_domains: 2, current_count: 0, custom_count: 0, can_select_more: true });
  const [selectingDomain, setSelectingDomain] = useState(null);
  const [releasingDomain, setReleasingDomain] = useState(null);
  const [domainSearch, setDomainSearch] = useState("");

  // Domain health scan state
  const [domainScanLoading, setDomainScanLoading] = useState(false);
  const [domainScanResults, setDomainScanResults] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/redirector/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
      setRotationEnabled(response.data.rotation_enabled !== false);
      setRotationMode(response.data.rotation_mode || "smart");
    } catch (error) {
      console.error("Error fetching redirector config:", error);
    }
  }, [token]);

  const [savingDomainDest, setSavingDomainDest] = useState(null);
  const saveDomainDestination = async (domain, destination) => {
    setSavingDomainDest(domain);
    try {
      await axios.put(`${API}/redirector/domain-destination`, { domain, destination }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchConfig();
    } catch (error) {
      alert(error.response?.data?.detail || "Error saving default destination");
    }
    setSavingDomainDest(null);
  };


  const fetchLinks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinks(response.data.links || []);
    } catch (error) {
      console.error("Error fetching links:", error);
    }
  }, [token]);

  const fetchCustomPaths = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/custom-paths`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomPaths(response.data.custom_paths || []);
      setGlobalPaths(response.data.global_paths || []);
      setMaxCustomPaths(response.data.max_custom_paths);
      setUsedCustomPaths(response.data.used_custom_paths);
    } catch (error) {
      console.error("Error fetching custom paths:", error);
    }
  }, [token]);

  const addCustomPath = async () => {
    if (!newCustomPath.trim()) return;
    setCustomPathLoading(true);
    try {
      await axios.post(`${API}/user/custom-paths`, { path: newCustomPath.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewCustomPath("");
      fetchCustomPaths();
    } catch (error) {
      alert(error.response?.data?.detail || "Error creating custom path");
    }
    setCustomPathLoading(false);
  };

  const deleteCustomPath = async (pathId) => {
    try {
      await axios.delete(`${API}/user/custom-paths/${pathId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCustomPaths();
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting custom path");
    }
  };

  const fetchAvailableDomains = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/available-domains`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableDomains(response.data.domains || []);
    } catch (error) {
      console.error("Error fetching available domains:", error);
    }
  }, [token]);

  const fetchDomainLimits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/domain-limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDomainLimits(response.data);
    } catch (error) {
      console.error("Error fetching domain limits:", error);
    }
  }, [token]);

  const selectDomain = async (domainId) => {
    setSelectingDomain(domainId);
    try {
      await axios.post(`${API}/user/select-domain`, { domain_id: domainId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await Promise.all([fetchConfig(), fetchAvailableDomains(), fetchDomainLimits()]);
    } catch (error) {
      alert(error.response?.data?.detail || "Error selecting domain");
    }
    setSelectingDomain(null);
  };

  const releaseDomain = async (domainId) => {
    if (!window.confirm("Release this domain? It will become available for other users.")) return;
    setReleasingDomain(domainId);
    try {
      await axios.delete(`${API}/user/release-domain/${domainId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await Promise.all([fetchConfig(), fetchAvailableDomains(), fetchDomainLimits()]);
    } catch (error) {
      alert(error.response?.data?.detail || "Error releasing domain");
    }
    setReleasingDomain(null);
  };

  const scanDomainsHealth = async () => {
    setDomainScanLoading(true);
    setDomainScanResults(null);
    try {
      const res = await axios.post(`${API}/user/domains/scan-health`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      setDomainScanResults(res.data.results || []);
    } catch (error) {
      alert(error.response?.data?.detail || "Error scanning domains");
    }
    setDomainScanLoading(false);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchConfig(), fetchLinks(), fetchCustomPaths(), fetchAvailableDomains(), fetchDomainLimits()]);
      setLoading(false);
    };
    loadData();
    
    // Auto-refresh links every 15 seconds
    const refreshInterval = setInterval(() => {
      fetchLinks();
      fetchAvailableDomains();
    }, 15000);
    
    return () => clearInterval(refreshInterval);
  }, [fetchAvailableDomains, fetchConfig, fetchCustomPaths, fetchDomainLimits, fetchLinks]);

  // Deep-link: /redirector?tab=my-domains (e.g. from the BYOD banner)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t && ["links", "custom-paths", "domains", "my-domains", "settings"].includes(t)) {
      setActiveTab(t);
    }
    if (params.get("action") === "create-link") {
      setActiveTab("links");
      setShowLinkForm(true);
    }
  }, []);


  const copyToClipboard = async (text, id) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(text);
    } catch (_error) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.setAttribute("readonly", "");
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (!copied) {
        toast.error("Copy failed", { description: "Select the text and copy it manually." });
        return;
      }
    }
    setCopiedItem(id);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const toggleRotation = async () => {
    const newValue = !rotationEnabled;
    setRotationEnabled(newValue);
    try {
      await axios.post(`${API}/redirector/rotation`, {
        enabled: newValue,
        mode: rotationMode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Error updating rotation:", error);
    }
  };

  const updateRotationMode = async (mode) => {
    setRotationMode(mode);
    try {
      await axios.post(`${API}/redirector/rotation`, {
        enabled: rotationEnabled,
        mode: mode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Error updating rotation mode:", error);
    }
  };

  // Link CRUD operations
  const createLink = async () => {
    const selectedDomains = linkForm.domains?.length
      ? linkForm.domains
      : linkForm.domain ? [linkForm.domain] : [];
    if (selectedDomains.length === 0) {
      alert("Please select at least one domain");
      return;
    }
    if (!config.redirect_url) {
      alert("Please set your redirect URL in Settings first");
      return;
    }
    try {
      // Use bulk endpoint to create links with all alias paths
      const response = await axios.post(`${API}/links/bulk`, {
        domains: selectedDomains,
        destination: linkForm.destination || undefined,
        name: linkForm.name,
        campaign_type: linkForm.campaign_type,
        pass_email: linkForm.pass_email,
        pass_email_format: linkForm.pass_email_format,
        track_b2b: linkForm.track_b2b
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowLinkForm(false);
      setLinkForm(freshLinkForm());
      await fetchLinks();
      setSelectedCampaignId(response.data.campaign_id);
      setSelectedVariantId(response.data.links?.[0]?.id || null);
      setMobileDetailOpen(true);
      
      // Show success message
      alert(`Success! ${response.data.campaign_name} created with ${response.data.created_count} variants across ${response.data.domain_count} domain(s).${response.data.skipped_count > 0 ? `\n(${response.data.skipped_count} existing paths skipped)` : ''}`);
    } catch (error) {
      if (error.response?.status === 503) await fetchLinks();
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : error.response
          ? `Error creating links (HTTP ${error.response.status})`
          : `Error creating links: ${error.message || "network request failed"}`;
      alert(message);
    }
  };

  const updateLink = async () => {
    if (!editingLink) return;
    try {
      await axios.put(`${API}/links/${editingLink.id}`, {
        destination: linkForm.destination,
        name: linkForm.name,
        campaign_type: linkForm.campaign_type,
        pass_email: linkForm.pass_email,
        pass_email_format: linkForm.pass_email_format,
        track_b2b: linkForm.track_b2b
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingLink(null);
      setLinkForm(freshLinkForm());
      fetchLinks();
    } catch (error) {
      if (error.response?.status === 503) {
        setEditingLink(null);
        setLinkForm(freshLinkForm());
        await fetchLinks();
      }
      alert(error.response?.data?.detail || "Error updating link");
    }
  };

  const deleteLink = async (linkId) => {
    setDeletingLink(true);
    setDeleteError("");
    try {
      const response = await axios.delete(`${API}/links/${linkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinks((current) => current.filter((link) => link.id !== linkId));
      setDeleteTarget(null);
      if (response.data?.sync && !response.data.sync.success) {
        setDeleteError("Link removed, but the redirect agent needs attention.");
      }
    } catch (error) {
      setDeleteError(error.response?.data?.detail || "Error deleting link");
    }
    setDeletingLink(false);
  };

  const toggleLinkActive = async (link) => {
    try {
      await axios.put(`${API}/links/${link.id}`, {
        is_active: !link.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLinks();
    } catch (error) {
      if (error.response?.status === 503) await fetchLinks();
      alert(error.response?.data?.detail || "Error updating link");
    }
  };

  const retryLinkSync = async (link) => {
    setRetryingLinkId(link.id);
    setSyncFeedback(null);
    setLinks((current) => current.map((item) => (
      item.id === link.id ? { ...item, sync_status: "syncing" } : item
    )));
    try {
      const response = await axios.post(`${API}/links/${link.id}/retry-sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const linkSync = response.data?.link_sync || {};
      setLinks((current) => current.map((item) => (
        item.id === link.id ? { ...item, ...linkSync } : item
      )));
      setSyncFeedback({ linkId: link.id, type: "success", text: "Agent delivery confirmed." });
      toast.success("Link synced", { description: `${link.domain} is up to date.` });
    } catch (error) {
      await fetchLinks();
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string" ? detail : "The redirect agent could not be reached.";
      setSyncFeedback({ linkId: link.id, type: "error", text: message });
      toast.error("Sync failed", { description: message });
    } finally {
      setRetryingLinkId(null);
    }
  };

  // Bulk selection functions
  const toggleSelectCampaign = (campaign) => {
    setSelectedLinks((current) => {
      const next = new Set(current);
      const campaignIds = campaign.variants.map((variant) => variant.id);
      const allSelected = campaignIds.every((id) => next.has(id));
      campaignIds.forEach((id) => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = filteredCampaigns.flatMap((campaign) => campaign.variants.map((variant) => variant.id));
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedLinks.has(id));
    setSelectedLinks((current) => {
      const next = new Set(current);
      visibleIds.forEach((id) => allVisibleSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const downloadSenderLinks = () => {
    const lines = links.map((link) => (
      link.campaign_type === "b2b" || link.pass_email === false
        ? getFullUrl(link)
        : getPlaceholderUrl(link)
    ));
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "sender_links.txt";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const bulkDeleteLinks = async () => {
    if (selectedLinks.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedLinks.size} link(s)? This cannot be undone.`)) return;
    
    setBulkDeleting(true);
    try {
      await axios.post(`${API}/links/bulk-delete`, {
        link_ids: Array.from(selectedLinks)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedLinks(new Set());
      fetchLinks();
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting links");
    }
    setBulkDeleting(false);
  };

  // Bulk campaign type toggle (B2B/DIRECT for all links)
  const toggleBulkCampaignType = async () => {
    const newType = bulkCampaignType === "direct" ? "b2b" : "direct";
    setUpdatingCampaignType(true);
    
    try {
      const response = await axios.post(`${API}/links/bulk-campaign-type`, {
        campaign_type: newType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBulkCampaignType(newType);
      fetchLinks();
      alert(`${response.data.message}`);
    } catch (error) {
      alert(error.response?.data?.detail || "Error updating campaign type");
    }
    setUpdatingCampaignType(false);
  };

  // Update bulkCampaignType based on majority of links when links load
  useEffect(() => {
    if (links.length > 0) {
      const b2bCount = links.filter(l => l.campaign_type === "b2b" || l.pass_email === false).length;
      const directCount = links.length - b2bCount;
      setBulkCampaignType(b2bCount > directCount ? "b2b" : "direct");
    }
  }, [links]);

  const startEdit = (link) => {
    setEditingLink(link);
    setLinkForm({
      domain: link.domain,
      domains: [link.domain],
      path: link.path,
      destination: link.destination,
      name: link.name || "",
      campaign_type: link.campaign_type || "direct",
      pass_email: link.pass_email !== false,
      pass_email_format: link.pass_email_format || "base64",
      track_b2b: link.track_b2b || false
    });
    setShowLinkForm(true);
  };

  const getFullUrl = (link) => {
    // Make sure we only add one slash between domain and path
    const cleanPath = link.path.startsWith('/') ? link.path.substring(1) : link.path;
    return `https://${link.domain}/${cleanPath}`;
  };

  // Generate link with [EMAIL64] placeholder for email senders (only for direct campaigns)
  const getPlaceholderUrl = (link) => {
    const cleanPath = link.path.startsWith('/') ? link.path.substring(1) : link.path;
    // B2B campaigns don't use email parameter
    if (link.campaign_type === 'b2b' || link.pass_email === false) {
      return `https://${link.domain}/${cleanPath}`;
    }
    return `https://${link.domain}/${cleanPath}?e=[EMAIL64]`;
  };

  // Generate test link with example email
  const getTestUrl = (link) => {
    const cleanPath = link.path.startsWith('/') ? link.path.substring(1) : link.path;
    const testEmail = "rharless@humphreyltd.com";
    const encodedEmail = btoa(testEmail).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return `https://${link.domain}/${cleanPath}?e=${encodedEmail}`;
  };

  const campaigns = useMemo(() => groupLinksIntoCampaigns(links), [links]);
  const filteredCampaigns = useMemo(() => filterCampaigns(campaigns, linksSearch), [campaigns, linksSearch]);
  const selectedCampaign = filteredCampaigns.find((campaign) => campaign.id === selectedCampaignId) || null;
  const visibleVariantIds = useMemo(
    () => filteredCampaigns.flatMap((campaign) => campaign.variants.map((variant) => variant.id)),
    [filteredCampaigns],
  );
  const allVisibleSelected = visibleVariantIds.length > 0 && visibleVariantIds.every((id) => selectedLinks.has(id));

  useEffect(() => {
    if (campaigns.length === 0 || filteredCampaigns.length === 0) {
      setSelectedCampaignId(null);
      setSelectedVariantId(null);
      setMobileDetailOpen(false);
      return;
    }
    const nextCampaign = filteredCampaigns.find((campaign) => campaign.id === selectedCampaignId) || filteredCampaigns[0];
    if (nextCampaign.id !== selectedCampaignId) setSelectedCampaignId(nextCampaign.id);
    if (!nextCampaign.variants.some((variant) => variant.id === selectedVariantId)) {
      setSelectedVariantId(nextCampaign.variants[0]?.id || null);
    }
  }, [campaigns.length, filteredCampaigns, selectedCampaignId, selectedVariantId]);

  const inspectCampaign = (campaign) => {
    setSelectedCampaignId(campaign.id);
    setSelectedVariantId(campaign.variants[0]?.id || null);
    setMobileDetailOpen(true);
    if (!campaign.variants.some((variant) => variant.id === syncFeedback?.linkId)) setSyncFeedback(null);
  };

  const selectedFormDomains = linkForm.domains?.length
    ? linkForm.domains
    : linkForm.domain ? [linkForm.domain] : [];
  const primaryFormDomain = selectedFormDomains[0] || linkForm.domain || "";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ===== Header ===== */}
      <div className="flex flex-col gap-5 border-b border-slate-800/80 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-3">
          <span className="wx-tactical"><Link2 className="h-3 w-3" /> Campaign ops</span>
          <h1 className="font-orbitron text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl" data-testid="redirector-title">Redirector Links</h1>
          <p className="max-w-xl text-sm text-slate-400">Manage your redirect links, domains and rotation strategy.</p>
        </div>
        <button
          onClick={() => {
            setEditingLink(null);
            setLinkForm(freshLinkForm(config.redirect_domains[0] || ""));
            setShowLinkForm(true);
          }}
          className="wx-btn-primary self-start sm:self-auto"
          disabled={config.redirect_domains.length === 0}
          data-testid="create-link-btn"
          title={config.redirect_domains.length === 0 ? "Claim or add a domain first" : "Create a new campaign link"}
        >
          <Plus className="h-4 w-4" />
          Create Link
        </button>
      </div>

      {/* ===== Tabs ===== */}
      <div className="flex flex-wrap gap-1 rounded-xl border border-slate-800/70 bg-[#0B1120]/70 p-1">
        {[
          { id: "links", label: "My Links", icon: Link2, count: links.length },
          { id: "custom-paths", label: "Custom Paths", icon: Route, count: usedCustomPaths },
          { id: "domains", label: "Assigned Domains", icon: Globe, count: config.redirect_domains.length },
          { id: "my-domains", label: "My Domains", icon: Globe, count: domainLimits.custom_count || 0 },
          { id: "settings", label: "Rotation Settings", icon: Shuffle }
        ].map(tab => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-all duration-150 ${
                active
                  ? "bg-gradient-to-r from-cyan-500/20 via-cyan-500/10 to-transparent text-cyan-100 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]"
                  : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100"
              }`}
              data-testid={`tab-${tab.id}`}
              aria-current={active ? "page" : undefined}
            >
              <tab.icon className={`h-4 w-4 ${active ? "text-cyan-300" : "text-slate-500"}`} />
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span className={`wx-chip ml-1 ${active ? "wx-chip-cyan" : "wx-chip-slate"}`}>{tab.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Link Form Modal */}
      {showLinkForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="link-form-modal">
          <div className="bg-slate-900 border border-cyan-500/30 rounded-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                {editingLink ? "Edit Link" : "Create Campaign Links"}
              </h3>
              <button
                onClick={() => {
                  setShowLinkForm(false);
                  setEditingLink(null);
                  setLinkForm(freshLinkForm());
                }}
                className="text-slate-400 hover:text-white"
                data-testid="link-form-close-btn"
                aria-label="Close campaign form"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Info about bulk creation - only show when creating new */}
              {!editingLink && (
                <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-cyan-300 font-medium">Auto-Generate Links</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Links will be automatically created for all alias paths configured by admin.
                        All links will redirect to your configured destination URL.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Domain selection */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">{editingLink ? "Domain" : "Campaign domains"}</label>
                {editingLink ? (
                  <Select value={linkForm.domain || undefined} disabled>
                    <SelectTrigger className="input-dark w-full" data-testid="link-domain-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value={linkForm.domain}>{linkForm.domain}</SelectItem></SelectContent>
                  </Select>
                ) : (
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-700 bg-slate-950 p-2" data-testid="link-domains-selector" role="group" aria-label="Campaign domains">
                    {config.redirect_domains.map((domain, index) => {
                      const checked = selectedFormDomains.includes(domain);
                      return (
                        <label key={domain} className={`flex cursor-pointer items-center gap-3 rounded px-2.5 py-2 text-sm transition-colors ${checked ? "bg-cyan-500/10 text-cyan-200" : "text-slate-400 hover:bg-slate-900 hover:text-white"}`} data-testid={`link-domain-option-${index}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const domains = checked
                                ? selectedFormDomains.filter((item) => item !== domain)
                                : [...selectedFormDomains, domain];
                              setLinkForm({ ...linkForm, domains, domain: domains[0] || "" });
                            }}
                            className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-cyan-500 focus:ring-cyan-500"
                            data-testid={`link-domain-checkbox-${index}`}
                          />
                          <span className="min-w-0 flex-1 truncate font-mono text-xs">{domain}</span>
                          {(config.domain_destinations || {})[domain] && <span className="text-[10px] text-emerald-400">DEFAULT</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
                {!editingLink && <p className="mt-1.5 text-xs text-slate-500" data-testid="selected-domain-count">{selectedFormDomains.length} domain{selectedFormDomains.length === 1 ? "" : "s"} selected</p>}
              </div>

              {/* Destination URL Display - shows configured URL from settings */}
              {!editingLink && (
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                  <label className="text-xs text-slate-400 mb-1 block">Destination URL (from Settings)</label>
                  
                  {config.multi_destination_urls_enabled && config.redirect_urls?.length > 1 ? (
                    <Select value={linkForm.destination || "__automatic__"} onValueChange={(destination) => setLinkForm({ ...linkForm, destination: destination === "__automatic__" ? "" : destination })}>
                      <SelectTrigger className="input-dark mt-1 w-full border-none p-0 font-mono text-sm text-cyan-400" data-testid="link-destination-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__automatic__">Use each domain default</SelectItem>
                        {Array.isArray(config.redirect_urls) && config.redirect_urls.map((url) => <SelectItem key={url} value={url}>{url}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-mono text-sm text-cyan-400 break-all" data-testid="link-destination-value">
                      {(config.domain_destinations || {})[primaryFormDomain] || config.redirect_url || <span className="text-red-400">Not configured - go to Settings first</span>}
                    </p>
                  )}
                  {selectedFormDomains.length > 1 && !linkForm.destination && <p className="mt-2 text-xs text-slate-500">Each domain uses its saved default, then your primary destination.</p>}
                </div>
              )}

              {/* Destination - only for editing existing links */}
              {editingLink && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Destination URL</label>
                  <input
                    type="url"
                    placeholder="https://your-destination.com/page"
                    value={linkForm.destination}
                    onChange={(e) => setLinkForm({ ...linkForm, destination: e.target.value })}
                    className="input-dark w-full"
                    data-testid="link-destination-input"
                  />
                </div>
              )}

              {/* Name (optional) */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Campaign Name (optional)</label>
                <input
                  type="text"
                  placeholder="My Campaign"
                  value={linkForm.name}
                  onChange={(e) => setLinkForm({ ...linkForm, name: e.target.value })}
                  className="input-dark w-full"
                  data-testid="link-name-input"
                />
              </div>

              {/* Campaign Type Selection */}
              <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <label className="text-sm text-white font-medium block mb-3">Campaign Type</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setLinkForm({ ...linkForm, campaign_type: 'direct', pass_email: true })}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                      linkForm.campaign_type === 'direct'
                        ? 'bg-cyan-500/10 border-cyan-500 text-cyan-400'
                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                    }`}
                    data-testid="campaign-type-direct-btn"
                  >
                    <div className="font-semibold">Direct Campaign</div>
                    <div className="text-xs mt-1 opacity-75">With email capture & pass-through</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setLinkForm({ ...linkForm, campaign_type: 'b2b', pass_email: false })}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                      linkForm.campaign_type === 'b2b'
                        ? 'bg-blue-500/10 border-blue-500 text-blue-300'
                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                    }`}
                    data-testid="campaign-type-b2b-btn"
                  >
                    <div className="font-semibold">B2B Campaign</div>
                    <div className="text-xs mt-1 opacity-75">Anonymous traffic, no sender needed</div>
                  </button>
                </div>
              </div>

              {/* Direct Campaign: Email Pass-through Options */}
              {linkForm.campaign_type === 'direct' && (
                <div className="p-4 bg-cyan-500/5 rounded-lg border border-cyan-500/20">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <label className="text-sm text-white font-medium">Auto-Pass Captured Email</label>
                      <p className="text-xs text-slate-500">Append captured email to destination URL</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLinkForm({ ...linkForm, pass_email: !linkForm.pass_email })}
                      className={`w-12 h-6 rounded-full transition-colors relative ${
                        linkForm.pass_email ? 'bg-cyan-500' : 'bg-slate-600'
                      }`}
                      data-testid="pass-email-toggle"
                      aria-pressed={linkForm.pass_email}
                    >
                      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        linkForm.pass_email ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  
                  {linkForm.pass_email && (
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => setLinkForm({ ...linkForm, pass_email_format: 'base64' })}
                        className={`flex-1 py-2 text-xs rounded-lg border transition-colors ${
                          linkForm.pass_email_format === 'base64'
                            ? 'bg-cyan-500/10 border-cyan-500 text-cyan-400'
                            : 'bg-slate-800 border-slate-700 text-slate-400'
                        }`}
                        data-testid="pass-email-format-base64-btn"
                      >
                        Base64 (Stealth)
                      </button>
                      <button
                        type="button"
                        onClick={() => setLinkForm({ ...linkForm, pass_email_format: 'plaintext' })}
                        className={`flex-1 py-2 text-xs rounded-lg border transition-colors ${
                          linkForm.pass_email_format === 'plaintext'
                            ? 'bg-cyan-500/10 border-cyan-500 text-cyan-400'
                            : 'bg-slate-800 border-slate-700 text-slate-400'
                        }`}
                        data-testid="pass-email-format-plaintext-btn"
                      >
                        Plaintext
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* B2B Campaign: Anonymous Tracking Options */}
              {linkForm.campaign_type === 'b2b' && (
                <div className="p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm text-white font-medium">Add Anonymous Tracking ID</label>
                      <p className="text-xs text-slate-500">Append random ID for click tracking analytics</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLinkForm({ ...linkForm, track_b2b: !linkForm.track_b2b })}
                      className={`w-12 h-6 rounded-full transition-colors relative ${
                        linkForm.track_b2b ? 'bg-blue-500' : 'bg-slate-600'
                      }`}
                      data-testid="b2b-tracking-toggle"
                      aria-pressed={linkForm.track_b2b}
                    >
                      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        linkForm.track_b2b ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  <p className="text-xs text-blue-300 mt-2">
                    B2B links work without sender email. Perfect for ads, social, and direct traffic.
                  </p>
                </div>
              )}

              {/* Preview - only show when editing */}
              {editingLink && linkForm.domain && (
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Current Link:</p>
                  <p className="font-mono text-sm text-cyan-400 break-all">
                    https://{linkForm.domain}/{editingLink.path}
                  </p>
                  {linkForm.campaign_type === 'direct' && linkForm.pass_email && (
                    <p className="text-xs text-slate-500 mt-2">
                      → Email will be passed as: ?e=[base64] or ?email=[email]
                    </p>
                  )}
                  {linkForm.campaign_type === 'b2b' && (
                    <p className="text-xs text-blue-300 mt-2">
                      → B2B mode: No email capture required
                    </p>
                  )}
                </div>
              )}

              {/* Preview for new links */}
              {!editingLink && selectedFormDomains.length > 0 && (linkForm.destination || config.redirect_url) && (
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-xs text-green-400 mb-1" data-testid="campaign-create-preview-count">One campaign across {selectedFormDomains.length} domain{selectedFormDomains.length === 1 ? "" : "s"}</p>
                  <p className="font-mono text-sm text-slate-300">
                    https://<span className="text-cyan-400">{primaryFormDomain}</span>/<span className="text-emerald-400">[alias-path]</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    → Redirecting to: <span className="text-cyan-400 break-all">{linkForm.destination || config.redirect_url}</span>
                  </p>
                </div>
              )}

              {/* Warning if no redirect URL configured */}
              {!editingLink && !config.redirect_url && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-xs text-red-400">
                    ⚠️ No redirect URL configured. Please go to <strong>Settings</strong> and set your destination URL first.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowLinkForm(false);
                  setEditingLink(null);
                }}
                className="btn-secondary flex-1"
                data-testid="link-form-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={editingLink ? updateLink : createLink}
                disabled={!editingLink && (!config.redirect_url || selectedFormDomains.length === 0)}
                className={`flex-1 flex items-center justify-center gap-2 btn-primary ${!editingLink && (!config.redirect_url || selectedFormDomains.length === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="link-form-submit"
              >
                {editingLink ? (
                  <>
                    <Save className="w-4 h-4" />
                    Update Link
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    Generate All Links
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Links Tab */}
      {activeTab === "links" && (
        <div className="space-y-4">
          {/* No domains warning */}
          {config.redirect_domains.length === 0 && (
            <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-400 mb-1">No Domains Assigned</h4>
                <p className="text-sm text-slate-300">
                  You need at least one domain assigned to create redirect links. Contact your administrator or submit a support request.
                </p>
              </div>
            </div>
          )}

          {/* Bulk Actions Bar */}
          {links.length > 0 && (
            <CampaignToolbar
              allSelected={allVisibleSelected}
              bulkCampaignType={bulkCampaignType}
              bulkDeleting={bulkDeleting}
              campaignsCount={campaigns.length}
              domainScanLoading={domainScanLoading}
              linksCount={links.length}
              onBulkDelete={bulkDeleteLinks}
              onDownload={downloadSenderLinks}
              onScan={scanDomainsHealth}
              onSelectAll={toggleSelectAll}
              onToggleCampaignType={toggleBulkCampaignType}
              searchTerm={linksSearch}
              selectedCount={selectedLinks.size}
              setSearchTerm={setLinksSearch}
              updatingCampaignType={updatingCampaignType}
              visibleCount={filteredCampaigns.length}
              visibleVariantCount={visibleVariantIds.length}
            />
          )}

          {/* Scan Results */}
          {domainScanResults && (
            <div className="p-3 bg-slate-800/60 rounded-lg border border-slate-700/40 space-y-1.5">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-slate-400 font-medium">Health Check Results</p>
                <button onClick={() => setDomainScanResults(null)} className="text-xs text-slate-500 hover:text-white" data-testid="dismiss-health-results-btn">Dismiss</button>
              </div>
              {domainScanResults.map((r, i) => (
                <div key={i} className={`flex items-center justify-between text-xs py-1.5 px-2 rounded ${
                  r.status === "flagged" ? "bg-red-500/10 text-red-400" : "bg-green-500/10 text-green-400"
                }`}>
                  <span className="font-mono truncate max-w-[70%]">{r.status === "flagged" ? "RED" : "OK"} — {r.name || r.path || r.domain}</span>
                  <span className="text-right flex-shrink-0">{r.issues?.length > 0 ? r.issues[0] : "No issues"}</span>
                </div>
              ))}
            </div>
          )}

          {/* Links List */}
          {links.length === 0 ? (
            <div className="text-center py-16 card-dark">
              <Link2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg mb-2">No redirect links yet</p>
              <p className="text-slate-500 text-sm mb-4">Create your first campaign to start redirecting traffic</p>
              {config.redirect_domains.length > 0 && (
                <button
                  onClick={() => {
                    setLinkForm(freshLinkForm(config.redirect_domains[0]));
                    setShowLinkForm(true);
                  }}
                  className="btn-primary"
                  data-testid="create-first-campaign-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Create First Campaign
                </button>
              )}
            </div>
          ) : (
            <div className="grid min-w-0 gap-5 lg:grid-cols-12" data-testid="links-split-view-container">
              <div className={`${mobileDetailOpen ? "hidden lg:block" : "block"} min-w-0 lg:col-span-5 xl:col-span-4`} data-testid="links-master-pane">
                <CampaignLinksMaster
                  campaigns={filteredCampaigns}
                  copiedItem={copiedItem}
                  copyToClipboard={copyToClipboard}
                  getFullUrl={getFullUrl}
                  onSelectCampaign={inspectCampaign}
                  onToggleSelectCampaign={toggleSelectCampaign}
                  selectedCampaignId={selectedCampaignId}
                  selectedLinks={selectedLinks}
                />
              </div>
              <div className={`${mobileDetailOpen ? "block" : "hidden lg:block"} min-w-0 lg:col-span-7 xl:col-span-8`}>
                <CampaignLinkDetailPane
                  campaign={selectedCampaign}
                  copiedItem={copiedItem}
                  copyToClipboard={copyToClipboard}
                  getFullUrl={getFullUrl}
                  getPlaceholderUrl={getPlaceholderUrl}
                  getTestUrl={getTestUrl}
                  onBack={() => setMobileDetailOpen(false)}
                  onDelete={(link) => { setDeleteError(""); setDeleteTarget(link); }}
                  onEdit={startEdit}
                  onRetrySync={retryLinkSync}
                  onSelectVariant={(variantId) => {
                    setSelectedVariantId(variantId);
                    if (syncFeedback?.linkId !== variantId) setSyncFeedback(null);
                  }}
                  onToggleActive={toggleLinkActive}
                  retryingLinkId={retryingLinkId}
                  selectedVariantId={selectedVariantId}
                  syncFeedback={syncFeedback}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Custom Paths Tab */}
      {activeTab === "custom-paths" && (
        <div className="space-y-4" data-testid="custom-paths-tab">
          {/* Plan limit info */}
          <div className="flex items-start gap-2 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
            <Info className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-cyan-300">
              <p>Create custom alias paths unique to your account. These work alongside the global paths set by the admin.</p>
              <p className="mt-1 font-medium">
                {maxCustomPaths === -1
                  ? `Unlimited custom paths (${usedCustomPaths} created)`
                  : `${usedCustomPaths} / ${maxCustomPaths} custom paths used`
                }
              </p>
            </div>
          </div>

          {/* Add Custom Path */}
          <div className="card-dark p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Add Custom Path</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={newCustomPath}
                onChange={(e) => setNewCustomPath(e.target.value)}
                placeholder="e.g. verify/account, login/secure, auth/confirm"
                className="input-dark flex-1 text-sm"
                onKeyDown={(e) => e.key === "Enter" && addCustomPath()}
                data-testid="custom-path-input"
              />
              <button
                onClick={addCustomPath}
                disabled={!newCustomPath.trim() || customPathLoading || (maxCustomPaths !== -1 && usedCustomPaths >= maxCustomPaths)}
                className="btn-primary flex items-center gap-2 text-sm"
                data-testid="add-custom-path-btn"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            {maxCustomPaths !== -1 && usedCustomPaths >= maxCustomPaths && (
              <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Limit reached. Upgrade your plan for more custom paths.
              </p>
            )}
          </div>

          {/* Your Custom Paths */}
          <div className="card-dark p-4">
            <h3 className="text-sm font-semibold text-white mb-3">
              Your Custom Paths ({customPaths.length})
            </h3>
            {customPaths.length === 0 ? (
              <p className="text-sm text-slate-500">No custom paths yet. Add one above.</p>
            ) : (
              <div className="space-y-2">
                {customPaths.map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/30" data-testid={`custom-path-${p.id}`}>
                    <div className="flex items-center gap-3">
                      <Route className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm text-white font-mono">/{p.path}</span>
                    </div>
                    <button
                      onClick={() => deleteCustomPath(p.id)}
                      className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-all"
                      data-testid={`delete-custom-path-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Global Paths (read-only) */}
          <div className="card-dark p-4">
            <h3 className="text-sm font-semibold text-white mb-3">
              Global Paths ({globalPaths.length})
              <span className="text-xs text-slate-500 ml-2 font-normal">Managed by admin — available to all users</span>
            </h3>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {globalPaths.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-2 bg-slate-800/30 rounded border border-slate-700/20">
                  <Globe className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-sm text-slate-400 font-mono">/{p.path}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Domains Tab - Self-Service Domain Selection */}
      {activeTab === "domains" && (
        <DomainSplitWorkspace
          config={config}
          links={links}
          domainLimits={domainLimits}
          availableDomains={availableDomains}
          selectingDomain={selectingDomain}
          releasingDomain={releasingDomain}
          copiedItem={copiedItem}
          savingDomainDest={savingDomainDest}
          onCopy={copyToClipboard}
          onSelect={selectDomain}
          onRelease={releaseDomain}
          onSaveDestination={saveDomainDestination}
          onCreateCampaign={(domain) => {
            setLinkForm(freshLinkForm(domain));
            setShowLinkForm(true);
          }}
        />
      )}

      {/* Legacy domain markup retained temporarily as a behavior reference during extraction. */}
      {false && activeTab === "domains" && (
        <div className="space-y-5" data-testid="domains-tab">
          {/* Domain Limit Indicator */}
          <div className="card-dark p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-500/15 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">Domain Slots</h3>
                  <p className="text-xs text-slate-500" data-testid="domain-slots-summary">
                    {domainLimits.current_count} of {domainLimits.max_domains} assigned domains used
                    {domainLimits.custom_count > 0
                      ? ` — plus ${domainLimits.custom_count} of your own custom domain${domainLimits.custom_count > 1 ? "s" : ""} (free, separate limit)`
                      : " — your own custom domains have a separate limit"}
                  </p>
                </div>
              </div>
              <span className={`text-sm font-mono px-3 py-1 rounded-full ${
                domainLimits.can_select_more
                  ? "bg-green-500/10 text-green-400 border border-green-500/30"
                  : "bg-red-500/10 text-red-400 border border-red-500/30"
              }`} data-testid="domain-slots-badge">
                {domainLimits.current_count}/{domainLimits.max_domains}
              </span>
            </div>
            {/* Progress bar */}
            <div className="h-2 bg-slate-700/60 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  domainLimits.current_count >= domainLimits.max_domains ? "bg-red-500" :
                  domainLimits.current_count >= domainLimits.max_domains - 1 ? "bg-yellow-500" : "bg-cyan-500"
                }`}
                style={{ width: `${Math.min((domainLimits.current_count / domainLimits.max_domains) * 100, 100)}%` }}
              />
            </div>
            {!domainLimits.can_select_more && (
              <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Upgrade your plan to unlock more domain slots.
              </p>
            )}
          </div>

          {/* Your Domains Section */}
          <div className="card-dark p-5" data-testid="my-domains-section">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-cyan-400" />
              Your Domains
            </h3>

            <div className="space-y-2">
              {config.redirect_domains.length === 0 ? (
                <div className="text-center py-8">
                  <Globe className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No domains selected yet</p>
                  <p className="text-slate-600 text-xs mt-1">Pick from the available pool below</p>
                </div>
              ) : (
                (config.domain_health || config.redirect_domains.map(d => ({ domain: d, health_score: 100, status: "healthy" }))).map((domainData, index) => {
                  const domain = typeof domainData === 'string' ? domainData : domainData.domain;
                  const healthScore = domainData.health_score ?? 100;
                  const status = domainData.status || "healthy";
                  const warning = domainData.warning;
                  const stats = domainData.stats || {};
                  const domainLinks = links.filter(l => l.domain === domain);
                  // Find the pool domain id for release
                  const domainId = domainData.domain_id;
                  const healthColors = {
                    healthy: { bg: "bg-green-500", text: "text-green-400" },
                    warning: { bg: "bg-yellow-500", text: "text-yellow-400" },
                    critical: { bg: "bg-red-500", text: "text-red-400" }
                  };
                  const colors = healthColors[status] || healthColors.healthy;
                  return (
                    <div key={domain} className="flex flex-col gap-2 p-3 bg-slate-800/40 border border-slate-700/40 rounded-lg hover:border-cyan-500/20 transition-all" data-testid={`my-domain-${index}`}>
                      <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-2 h-2 rounded-full ${colors.bg} flex-shrink-0`} />
                        <div className="min-w-0">
                          <p className="font-mono text-sm text-white truncate">{domain}</p>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-xs text-slate-500">{stats.link_count || domainLinks.length} links</span>
                            <span className="text-xs text-slate-500">{stats.total_uses || 0} uses</span>
                            <span className={`text-xs ${colors.text}`}>{healthScore}%</span>
                          </div>
                          {warning && (
                            <p className={`text-xs mt-1 ${colors.text} flex items-center gap-1`}>
                              <AlertTriangle className="w-3 h-3" />{warning}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        <button onClick={() => copyToClipboard(domain, `domain-${index}`)} className="text-slate-500 hover:text-cyan-400 p-1.5 transition-colors" title="Copy">
                          {copiedItem === `domain-${index}` ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => { setLinkForm(freshLinkForm(domain)); setShowLinkForm(true); }} className="text-slate-500 hover:text-green-400 p-1.5 transition-colors" title="Create campaign">
                          <Zap className="w-3.5 h-3.5" />
                        </button>
                        {domainId && (
                          <button
                            onClick={() => releaseDomain(domainId)}
                            disabled={releasingDomain === domainId}
                            className="text-slate-500 hover:text-red-400 p-1.5 transition-colors disabled:opacity-50"
                            title="Release domain"
                            data-testid={`release-domain-${index}`}
                          >
                            {releasingDomain === domainId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                      </div>
                      {config.multi_destination_urls_enabled && (config.redirect_urls?.length > 1) && (
                        <div className="flex items-center gap-2 pl-4 border-t border-slate-700/30 pt-2" data-testid={`domain-default-dest-row-${index}`}>
                          <span className="text-xs text-slate-500 whitespace-nowrap">Default destination:</span>
                          <select
                            value={(config.domain_destinations || {})[domain] || ""}
                            onChange={(e) => saveDomainDestination(domain, e.target.value)}
                            disabled={savingDomainDest === domain}
                            className="input-dark text-xs py-1 flex-1 min-w-0 text-cyan-400 font-mono disabled:opacity-50"
                            data-testid={`domain-default-dest-select-${index}`}
                          >
                            <option value="">No default (choose per link)</option>
                            {(config.redirect_urls || []).filter(u => u).map((url, i) => (
                              <option key={i} value={url}>{url}</option>
                            ))}
                          </select>
                          {savingDomainDest === domain && <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-400" />}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Available Domain Pool */}
          <div className="card-dark p-5" data-testid="available-domains-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-white flex items-center gap-2">
                <Server className="w-4 h-4 text-purple-400" />
                Available Domain Pool
                <span className="text-xs text-slate-500 font-normal ml-1">({availableDomains.length})</span>
              </h3>
              {availableDomains.length > 3 && (
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={domainSearch}
                    onChange={(e) => setDomainSearch(e.target.value)}
                    placeholder="Filter..."
                    className="bg-slate-800/60 border border-slate-700/50 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:border-cyan-500/40 focus:outline-none w-44"
                    data-testid="domain-pool-search"
                  />
                </div>
              )}
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {availableDomains.length === 0 ? (
                <div className="py-4">
                  <div className="mx-auto flex max-w-sm flex-col items-center rounded-2xl border border-dashed border-slate-800 bg-[#0B1120]/50 px-5 py-10 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full border border-slate-700/50 bg-slate-800/60 text-cyan-400">
                      <Server className="h-6 w-6" />
                    </div>
                    <h3 className="mt-3 text-base font-bold text-slate-200">Pool is fully allocated</h3>
                    <p className="mt-2 text-xs leading-relaxed text-slate-400">
                      Every shared domain is already claimed. Ask your admin to top up the pool or reclaim idle assignments to free capacity.
                    </p>
                  </div>
                </div>
              ) : (
                availableDomains
                  .filter(d => !domainSearch || d.domain.toLowerCase().includes(domainSearch.toLowerCase()))
                  .map((d, index) => (
                    <div key={d.id} className="flex items-center justify-between p-3 bg-slate-800/30 border border-slate-700/30 rounded-lg hover:border-purple-500/20 transition-all" data-testid={`available-domain-${index}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Globe className="w-4 h-4 text-purple-400/60 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-mono text-sm text-slate-300 truncate">{d.domain}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {d.ssl_active && <span className="text-[10px] px-1.5 py-0.5 bg-green-500/10 text-green-500 rounded">SSL</span>}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => selectDomain(d.id)}
                        disabled={!domainLimits.can_select_more || selectingDomain === d.id}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          domainLimits.can_select_more
                            ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20"
                            : "bg-slate-700/30 text-slate-600 border border-slate-700/30 cursor-not-allowed"
                        } disabled:opacity-50`}
                        data-testid={`select-domain-${index}`}
                      >
                        {selectingDomain === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        Select
                      </button>
                    </div>
                  ))
              )}
            </div>
          </div>

        </div>
      )}

      {/* Settings Tab */}
      {/* My Domains Tab (BYOD - bring your own domain) */}
      {activeTab === "my-domains" && (
        <div className="space-y-4" data-testid="my-domains-byod-tab">
          <CustomDomainsPanel token={token} />
          <p className="text-xs text-slate-500 px-1">
            Once activated, your own domain appears under <b className="text-slate-400">Assigned Domains</b> and works exactly like a domain assigned by an admin — create links and rotate on it right away.
            Your custom domains have their own separate limit and do <b className="text-slate-400">not</b> use up your {domainLimits.max_domains} admin-assigned domain slots.
          </p>
        </div>
      )}

      {activeTab === "settings" && (
        <div className="space-y-6">
          {/* Smart Rotation Control */}
          <div className="card-dark p-6" data-testid="rotation-control">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Shuffle className="w-6 h-6 text-cyan-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">Smart Domain Rotation</h3>
                  <p className="text-xs text-slate-400">Automatically rotate domains to prevent burning</p>
                </div>
              </div>
              <button
                onClick={toggleRotation}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  rotationEnabled ? 'bg-cyan-500' : 'bg-slate-700'
                }`}
                data-testid="rotation-toggle"
              >
                <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                  rotationEnabled ? 'left-8' : 'left-1'
                }`} />
              </button>
            </div>

            {rotationEnabled && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  {[
                    { id: "smart", label: "Smart", icon: Zap, desc: "AI-powered distribution based on domain health" },
                    { id: "round-robin", label: "Round Robin", icon: Activity, desc: "Equal distribution across all domains" },
                    { id: "random", label: "Random", icon: Shuffle, desc: "Random domain selection for each request" }
                  ].map(mode => (
                    <button
                      key={mode.id}
                      onClick={() => updateRotationMode(mode.id)}
                      className={`flex-1 p-3 rounded-lg border transition-all ${
                        rotationMode === mode.id
                          ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-400'
                          : 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:border-slate-600'
                      }`}
                    >
                      <mode.icon className="w-4 h-4 mx-auto mb-1" />
                      <span className="text-xs font-medium block">{mode.label}</span>
                    </button>
                  ))}
                </div>
                
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-400" />
                    <span className="text-xs text-green-400">
                      {rotationMode === "smart" && "Smart rotation analyzes domain reputation and distributes traffic to protect your domains"}
                      {rotationMode === "round-robin" && "Round-robin ensures equal traffic distribution across all domains"}
                      {rotationMode === "random" && "Random selection makes traffic patterns unpredictable"}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Current Config */}
          <div className="card-dark p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-cyan-400" />
              Current Configuration
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                <p className="text-xs text-slate-500 mb-1">Default Redirect URL</p>
                <p className="font-mono text-sm text-cyan-400 break-all">
                  {config.redirect_url || "Not set"}
                </p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                <p className="text-xs text-slate-500 mb-1">Landing Page Template</p>
                <p className="font-mono text-sm text-slate-300">
                  {config.current_preview || "blank"}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4">
              * Update these settings in the Settings page
            </p>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/80 p-4" data-testid="delete-link-modal">
          <div className="w-full max-w-md rounded-md border border-red-500/30 bg-slate-900 p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-red-500/10">
                <Trash2 className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white" data-testid="delete-link-modal-title">Delete campaign link?</h3>
                <p className="mt-2 break-all text-sm text-slate-400" data-testid="delete-link-modal-path">
                  {deleteTarget.domain}/{String(deleteTarget.path || "").replace(/^\//, "")}
                </p>
              </div>
            </div>
            <p className="mt-5 text-sm text-slate-300">The link will disappear from your dashboard and the path will be disabled on its redirect agent.</p>
            {deleteError && <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300" data-testid="delete-link-error">{deleteError}</p>}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deletingLink} data-testid="delete-link-cancel-btn" className="rounded-md border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800">Cancel</button>
              <button type="button" onClick={() => deleteLink(deleteTarget.id)} disabled={deletingLink} data-testid="delete-link-confirm-btn" className="flex items-center justify-center gap-2 rounded-md bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-400 disabled:opacity-60">
                {deletingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {deletingLink ? "Deleting…" : "Delete link"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedirectorLinks;
